import { h, Component, Prop, Host, State } from '@stencil/core';

import { getAbsoluteAssetPath, hasSlotContent, getCSSVariable } from '../../../services/utils';

@Component({
  tag: 'pk-card',
  styleUrl: './scss/pk-card.scss',
  assetsDirs: ['assets'],
  shadow: true,
})
export class PkCard {
  // Own Properties
  // ====================

  private cssComponentPrefix = '--card';
  private bodyContainerElem: HTMLDivElement;
  private headingContainerElem: HTMLDivElement;

  // Reference to host HTML element
  // ===================================

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() hasBody: boolean = true;
  @State() hasHeading: boolean = true;

  // Public Property API
  // ========================

  /** Whether if the content should be over the heading or not */
  @Prop({ reflect: true }) content?: 'default' | 'over' = 'default';

  /** If `true`, a shadow elevation will be displayed around the card, and no border stroke will be shown */
  @Prop({ reflect: true }) elevation?: boolean = false;

  /** Custom height value for the card. */
  @Prop({ reflect: true }) height?: number;

  /** It will define the card orientation. */
  @Prop({ reflect: true }) orientation?: 'portrait' | 'horizontal' = 'portrait';

  /** If `false`, no border stroke will be shown */
  @Prop() stroke: boolean = true;

  /** If true, the sponsor layout will be provided. */
  @Prop({ reflect: true }) sponsor: boolean = false;

  /** URL value of the sponsor image. */
  @Prop({ reflect: true }) sponsorImage: string;

  /** Where to position the sponsor image. */
  @Prop({ reflect: true }) sponsorPosition: 'default' | 'top-left' | 'top-right' = 'default';

  /** Will set the sponsor strip (line/gradient) color. */
  @Prop({ reflect: true }) sponsorStripColor: string;

  /** If `true`, the card background color will be transparent
   * and the heading thumbnail will be shown with rounded corners */
  @Prop() transparency: boolean = false;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.handleBodySlotChange();
    this.hasHeadingSlot();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleBodySlotChange = () => {
    if (!this.bodyContainerElem) return;

    this.hasBody = hasSlotContent(this.bodyContainerElem);
  };

  private hasHeadingSlot = () => {
    if (!this.headingContainerElem) return;

    this.hasHeading = hasSlotContent(this.headingContainerElem);
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.height && { [`${this.cssComponentPrefix}--height`]: `${this.height}px` }),
      ...(this.sponsor &&
        this.sponsorStripColor && {
          [`${this.cssComponentPrefix}--sponsor-strip-color`]: getCSSVariable(this.sponsorStripColor),
        }),
    };

    return (
      <Host class={{ 'has-sponsor': this.sponsor, [`sponsor-${this.sponsorPosition}`]: true }} style={{ ...style }}>
        <div
          class={{
            'card--wrapper': true,
            [`${this.orientation}`]: true,
            'no-body': !this.hasBody,
            '!border-solid': this.stroke,
            'with-elevation': this.elevation,
            'has-transparency': this.transparency,
          }}
          part="base"
        >
          <div
            class={{
              'card--heading__outline': true,
              hidden: !this.hasHeading && !this.sponsor && this.content !== 'over',
              [`${this.orientation}`]: true,
              'with-elevation': this.elevation,
              'has-transparency': this.transparency,
              // In here we guarantee that the header get expanded to the card height
              // even in portrait mode for those cases like nominee details
              // https://zeroheight.com/4a7979wvg/p/400dc1-card/t/52ab6c
              'content-over': this.content === 'over' && this.orientation !== 'horizontal',
            }}
            part="heading"
            ref={(el: HTMLDivElement) => (this.headingContainerElem = el)}
          >
            {this.sponsor && this.sponsorImage && (
              <pk-image
                part="sponsor"
                class={{
                  'sponsor-image': true,
                  hidden: this.orientation === 'horizontal' && this.sponsorPosition === 'default',
                }}
                alt="sponsor"
                src={this.sponsorImage}
                fallbackImage={getAbsoluteAssetPath('./assets/sponsor-placeholder.svg')}
              />
            )}
            <slot name="heading" />
          </div>
          <div
            class={{
              'card--body__outline': true,
              [`${this.orientation}`]: true,
              // content-over can be ONLY applied if the card is NOT in horizontal mode
              'content-over': this.content === 'over' && this.orientation !== 'horizontal',
            }}
            ref={(el: HTMLDivElement) => (this.bodyContainerElem = el)}
            part="body"
          >
            <slot name="body" onSlotchange={this.handleBodySlotChange} />
          </div>
        </div>
      </Host>
    );
  }
}
